/* eslint-disable max-len */
import React, { memo, useState, useEffect, useCallback } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, string, bool } from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import isCssApplied from 'Utils/isCssApplied'

import Carousel from './Components/Carousel/Carousel'
import Header from './Components/Header/Header'

// Loaded on demand when the user opens the fullscreen zoom: the component is
// excluded from the item page's eager bundle (see Packs/new_item_page.jsx)
// and is never part of the SSR output (it only renders after interaction).
let FullScreenGallery

// The fullscreen layout rules live in the page's deferred full stylesheet
// (appended on window.load, see _fast_partials/_custom_head_styles). Opening
// the zoom before they arrive renders an unstyled, duplicated gallery, so the
// zoom stays inert until the modifier styles are applied.
const areFullScreenStylesReady = () => (
  isCssApplied('gallery-area gallery-area--full-screen', 'position', 'fixed')
)

// `undefined` until any config lands, so the overlay keeps the SSR value until then.
const selectViewerIsAdult = (config) => (config ? Boolean(config.viewerIsAdult) : undefined)

function GalleryArea({
  medias,
  title,
  displayParentalLockOverlay,
  extractingCoreStyles = false,
  showAutoGeneratedImages = false,
}) {
  const [shouldDisplayParentalLockOverlay, setShouldDisplayParentalLockOverlay] = useState(displayParentalLockOverlay)
  const [activeIndex, setActiveIndex] = useState(0)
  const [fullScreenGallery, setFullScreenGallery] = useState(false)
  const [isFullScreenGalleryReady, setIsFullScreenGalleryReady] = useState(false)
  const [isActiveMediaLoading, setIsActiveMediaLoading] = useState(true)
  const [loadedSlides, setLoadedSlides] = useState(new Set())
  const totalCount = medias.length

  const viewerIsAdult = usePageConfig({ select: selectViewerIsAdult })

  useEffect(() => {
    if (viewerIsAdult === undefined) {
      return
    }

    setShouldDisplayParentalLockOverlay(!viewerIsAdult && displayParentalLockOverlay)
  }, [viewerIsAdult, displayParentalLockOverlay])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const handleKeyDown = (event) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && fullScreenGallery) {
        setFullScreenGallery(false)
        if (document.body) {
          document.body.classList.remove('disable-page-scroll')
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullScreenGallery])

  const handleActiveSlide = useCallback((index) => {
    setActiveIndex(index)
    // Only show loading if this slide hasn't been loaded before
    setIsActiveMediaLoading(!loadedSlides.has(index))
  }, [loadedSlides])

  const handleFullScreenGalleryIndex = useCallback((index) => {
    setActiveIndex(index)
    // Only show loading if this slide hasn't been loaded before
    setIsActiveMediaLoading(!loadedSlides.has(index))
  }, [loadedSlides])

  // Unlike createDeferredMounter (Utils/createDeferredMounter), which works via
  // imperative onMount() callbacks for content outside the React tree,
  // FullScreenGallery renders inside this component's own tree and needs
  // isFullScreenGalleryReady as React state to trigger a re-render.
  const ensureFullScreenGallery = useCallback(() => {
    if (FullScreenGallery) {
      setIsFullScreenGalleryReady(true)
      return
    }

    // No webpackPrefetch: its runtime calls document.head.appendChild() during SSR bundle
    // evaluation, which forced a global `document` stub that in turn made
    // @floating-ui/react-dom's `typeof document !== 'undefined'` guard pick the real
    // useLayoutEffect on the server (SSR warning on every Triangle UI Dropdown).
    import(/* webpackChunkName: "fullscreen-gallery" */ './Components/FullScreenGallery/FullScreenGallery')
      .then((module) => {
        FullScreenGallery = module.default
        setIsFullScreenGalleryReady(true)
      })
      .catch((error) => {
        // Chunk failed to load: close the fullscreen state so the gallery
        // layout and page scroll are restored; zoom retries on the next click.
        setFullScreenGallery(false)
        if (window.sendErrorToSentry) {
          window.sendErrorToSentry(error, { tags: { section: 'fullscreen-gallery-chunk' } })
        }
      })
  }, [])

  const openFullScreenGallery = useCallback(() => {
    if (!areFullScreenStylesReady()) {
      return
    }

    ensureFullScreenGallery()
    setFullScreenGallery(true)
  }, [ensureFullScreenGallery])

  const handleActiveMediaLoaded = useCallback(() => {
    setIsActiveMediaLoading(false)
    setLoadedSlides((prev) => new Set(prev).add(activeIndex))
  }, [activeIndex])

  const triggerFullScreenGallery = useCallback(() => {
    // Closing must always work; only opening waits for the deferred styles.
    if (!fullScreenGallery && !areFullScreenStylesReady()) {
      return
    }

    ensureFullScreenGallery()
    setFullScreenGallery((prevState) => !prevState)
  }, [fullScreenGallery, ensureFullScreenGallery])

  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) {
      return undefined
    }

    if (fullScreenGallery && isFullScreenGalleryReady) {
      document.body.classList.add('disable-page-scroll')
    } else {
      document.body.classList.remove('disable-page-scroll')
    }

    return () => {
      document.body.classList.remove('disable-page-scroll')
    }
  }, [fullScreenGallery, isFullScreenGalleryReady])

  // Safety timeout - clear loading after 3s even if callback never fires
  useEffect(() => {
    if (!isActiveMediaLoading) {
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setIsActiveMediaLoading(false)
      setLoadedSlides((prev) => new Set(prev).add(activeIndex))
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [activeIndex, isActiveMediaLoading])

  return (
    <div className={
      classnames(
        'gallery-area',
        fullScreenGallery && 'gallery-area--full-screen'
      )}
    >
      <div className="gallery-area__header-container">
        <Header
          fullScreenGalleryIndex={activeIndex}
          activeIndex={activeIndex}
          totalCount={totalCount}
          fullScreenGallery={fullScreenGallery}
          triggerFullScreenGallery={triggerFullScreenGallery}
        />
      </div>
      <Carousel
        title={title}
        medias={medias}
        onActiveIndexChange={handleActiveSlide}
        activeIndex={activeIndex}
        fullScreenGallery={fullScreenGallery}
        shouldDisplayParentalLockOverlay={shouldDisplayParentalLockOverlay}
        setShouldDisplayParentalLockOverlay={setShouldDisplayParentalLockOverlay}
        onActiveSlideDoubleClick={openFullScreenGallery}
        extractingCoreStyles={extractingCoreStyles}
        onActiveMediaLoaded={handleActiveMediaLoaded}
        isActiveMediaLoading={isActiveMediaLoading}
        showAutoGeneratedImages={showAutoGeneratedImages}
      />
      {fullScreenGallery && isFullScreenGalleryReady && (
        <FullScreenGallery
          medias={medias}
          onClose={triggerFullScreenGallery}
          title={title}
          fullScreenGalleryIndex={activeIndex}
          handleFullScreenGalleryIndex={handleFullScreenGalleryIndex}
          onActiveMediaLoaded={handleActiveMediaLoaded}
          isActiveMediaLoading={isActiveMediaLoading}
        />
      )}
    </div>
  )
}

GalleryArea.propTypes = {
  medias: array.isRequired,
  title: string.isRequired,
  displayParentalLockOverlay: bool.isRequired,
  extractingCoreStyles: bool,
  showAutoGeneratedImages: bool,
}

export default memo(GalleryArea)
